import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../api';
import type { ApiErrorResponse, ApiResponse } from '../../types';
import { apiDataResponseMapper, apiPayloadMapper } from '../../utils';
import { ClusterStatus } from './enums';
import {
  addHeapBoundaryPayloadMapper,
  updateHeapPayloadMapper,
} from './mapper';
import type {
  AddHeapBoundaryPayload,
  DeleteHeapListPayload,
  DownloadHeapsReportPayload,
  DownloadVolumeReportPayload,
  GetHeapListPayload,
  HeapClusterListResponseDataObj,
  HeapClusterResponseDataTypeObj,
  HeapIdPayload,
  HeapListResponseDataType,
  HeapListResponseDataTypeObj,
  HeapObj,
  HeapRenamePayload,
} from './types';

// Apis.
export const getHeapListRequest = async (
  payload: HeapIdPayload,
): Promise<HeapClusterListResponseDataObj> => {
  const res = await api.get('/iterations/heap-boundary/', {
    params: {
      iteration_id: payload.id,
    },
  });
  res.data = apiDataResponseMapper(res.data);
  return res;
};

export const deleteHeapRequest = async (
  payload: HeapIdPayload,
): Promise<ApiResponse> =>
  await api.delete(`/iterations/heap-boundary/${payload.id}/`);

export const addHeapRequest = async (
  payload: AddHeapBoundaryPayload,
): Promise<ApiResponse> => {
  const response = await api.post<ApiResponse, any>(
    '/iterations/heap-boundary/',
    addHeapBoundaryPayloadMapper(payload),
  );
  response.data = apiDataResponseMapper(response.data);
  return response;
};

export const getHeapRequest = async ({
  queryKey,
}): Promise<ApiResponse<HeapClusterResponseDataTypeObj>> => {
  const [, heapId] = queryKey;
  const res = await api.get<ApiResponse, any>(
    `/iterations/heap-boundary/${heapId}/`,
  );
  res.data = apiDataResponseMapper(res.data);
  return res;
};

export const downloadHeapRequest = async (
  heapId: string,
): Promise<ApiResponse> =>
  await api.get<ApiResponse, any>(
    `/iterations/heap-boundary/${heapId}/download/`,
    {
      responseType: 'blob',
    },
  );

export const updateHeapRequest = async (
  payload: HeapObj,
): Promise<ApiResponse> =>
  await api.patch(
    `/iterations/heap-boundary/${payload.id}/`,
    updateHeapPayloadMapper(payload),
  );

export const recalculateHeapVolumeRequest = async (
  payload: HeapIdPayload,
): Promise<ApiResponse> => {
  const res = await api.post<ApiResponse, any>(
    `/iterations/heap-boundary/${payload.id}/recalculate-volume/`,
  );
  res.data = apiDataResponseMapper(res.data);
  return res;
};

export const downloadVolumeReportRequest = async (
  payload: DownloadVolumeReportPayload,
): Promise<Blob> =>
  await api.get(
    `/iterations/heap-boundary/volume-report/?ids=${payload.heapIds}&response_format=${payload.responseFormat}`,
    {
      responseType: 'blob',
    },
  );

export const getAllHeapListRequest = async (
  payload: GetHeapListPayload,
): Promise<HeapListResponseDataTypeObj> => {
  const mappedPayload = apiPayloadMapper(payload);
  const res = await api.get('/iterations/heaps/', {
    params: mappedPayload,
  });
  res.data = apiDataResponseMapper(res.data);
  return res.data;
};

export const downloadHeapsReportRequest = async (
  payload: DownloadHeapsReportPayload,
): Promise<Blob> => {
  const mappedPayload = apiPayloadMapper(payload);
  return await api.get('/iterations/heaps/bulk/', {
    params: mappedPayload,
  });
};

export const deleteHeapListRequest = async (
  payload: DeleteHeapListPayload,
): Promise<ApiResponse> => {
  const mappedPayload = apiPayloadMapper(payload);
  return await api.delete('/iterations/heaps/bulk/', { data: mappedPayload });
};

export const sendRenameHeapRequest = async (
  payload: HeapRenamePayload,
): Promise<HeapListResponseDataType> => {
  const res = await api.patch(`/iterations/heaps/${payload.heapId}/`, {
    name: payload.heapName,
  });
  return apiDataResponseMapper(res.data);
};

// Hooks.
export const useHeapList = (payload: HeapIdPayload, enabled = true) =>
  useQuery<HeapClusterListResponseDataObj, ApiErrorResponse>({
    queryKey: ['iterations', payload],
    queryFn: () => getHeapListRequest(payload),
    enabled,
    refetchInterval: (data) => {
      if (!data?.state?.data) {
        return false;
      }
      const isAnyHeapBeingCalculated = data?.state?.data?.data?.some(
        (heap) => heap.status === ClusterStatus.Processing,
      );

      return isAnyHeapBeingCalculated ? 2000 : false;
    },
  });

export const useHeap = (heapId: string, enabled?: boolean) =>
  useQuery<any, ApiErrorResponse, ApiResponse<HeapClusterResponseDataTypeObj>>({
    queryKey: ['/iterations/heap-boundary/', heapId],
    queryFn: getHeapRequest,
    enabled,
  });

export const useDownloadHeap = () =>
  useMutation<any, ApiErrorResponse, string>({
    mutationFn: downloadHeapRequest,
  });

export const useAddHeap = () =>
  useMutation<ApiResponse, ApiErrorResponse, any>({
    mutationFn: addHeapRequest,
  });

export const useUpdateHeap = () =>
  useMutation<ApiResponse, ApiErrorResponse, HeapObj>({
    mutationFn: async (payload: HeapObj) => updateHeapRequest(payload),
  });

export const useDeleteHeap = () =>
  useMutation<ApiResponse, ApiErrorResponse, HeapIdPayload>({
    mutationFn: deleteHeapRequest,
  });

export const useRecalculateHeapVolume = () =>
  useMutation<ApiResponse, ApiErrorResponse, HeapIdPayload>({
    mutationFn: recalculateHeapVolumeRequest,
  });

export const useDownloadVolumeReport = () =>
  useMutation<Blob, ApiErrorResponse, DownloadVolumeReportPayload>({
    mutationFn: downloadVolumeReportRequest,
  });

// Todo:Rename this hook according to Heap and HeapCluster.
export const useFetchHeapClusterList = (payload: GetHeapListPayload) =>
  useQuery<HeapListResponseDataTypeObj, ApiResponse>({
    queryKey: ['iterations', payload],
    queryFn: () => getAllHeapListRequest(payload),
  });

export const useDownloadHeapsReport = () =>
  useMutation<Blob, ApiErrorResponse, DownloadHeapsReportPayload>({
    mutationFn: downloadHeapsReportRequest,
  });

export const useDeleteHeapList = () =>
  useMutation<ApiResponse, ApiErrorResponse, DeleteHeapListPayload>({
    mutationFn: deleteHeapListRequest,
  });

export const useRenameHeap = () =>
  useMutation<HeapListResponseDataType, ApiErrorResponse, HeapRenamePayload>({
    mutationFn: (payload: HeapRenamePayload) => sendRenameHeapRequest(payload),
  });
